import React from "react";
import { FaPassport } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { visaDestinations } from "../../content/indiaSiteContent";
import { cmsApi } from "../../services/cms";
import { useCMS } from "../../hooks/useCMS";
import { filterByCountrySelection } from "../../utils/countryFilter";

type VisaDestinationChip = {
  slug: string;
  title: string;
  countries?: string[] | null;
  country?: string | null;
  leadCountry?: string | null;
  siteCountry?: string | null;
  sourceCountry?: string | null;
  market?: string | null;
};

const getVisaCountrySelection = (destination: VisaDestinationChip) =>
  destination.countries ||
  destination.country ||
  destination.leadCountry ||
  destination.siteCountry ||
  destination.sourceCountry ||
  destination.market ||
  null;

const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  const { data: visaDestinationsFromCms } =
    useCMS<VisaDestinationChip[]>(cmsApi.getVisaDestinations);

  const handleApplyNow = () => {
    const target = document.getElementById("visa-application-form");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const filteredVisaDestinations = filterByCountrySelection(
    (visaDestinationsFromCms && visaDestinationsFromCms.length > 0
      ? visaDestinationsFromCms
      : (visaDestinations as unknown as VisaDestinationChip[])),
    getVisaCountrySelection,
    import.meta.env.VITE_SITE_REGION || "IN"
  );

  return (
    <section className="relative overflow-hidden bg-brand-dark pb-20 pt-32 md:pb-32 md:pt-48">
      <div className="absolute inset-0 opacity-40">
        <img
          src="/3.jpg"
          alt="Travel Background"
          className="h-full w-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/70 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <span className="mb-6 inline-block rounded-full border border-brand-pink/30 bg-brand-pink/20 px-3 py-1 text-sm font-semibold uppercase tracking-wider text-brand-pink">
          Visa Services
        </span>

        <h1 className="mb-6 font-heading text-4xl font-bold leading-tight text-white md:text-6xl lg:text-7xl">
          Visa assistance for
          <br />
          <span className="italic text-brand-pink">international travel from India</span>
        </h1>

        <p className="mx-auto mb-10 max-w-3xl text-lg text-gray-300 md:text-xl">
          Apply with guided support for Schengen, UK, USA, Australia, and
          Singapore travel. We help simplify the process so you can focus on the
          trip ahead.
        </p>

        <div className="mb-10 flex flex-wrap justify-center gap-3">
          {filteredVisaDestinations.slice(0, 5).map((destination) => (
            <Link
              key={destination.slug}
              to={`/visa-services/${destination.slug}`}
              className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              {t(destination.title)}
            </Link>
          ))}
        </div>

        <button
          type="button"
          onClick={handleApplyNow}
          className="inline-flex items-center gap-3 rounded-full bg-brand-pink px-8 py-4 font-medium text-white transition-colors hover:bg-brand-pink/90"
        >
          <FaPassport />
          {t("Apply Now")}
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
